import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { theme } from '../theme';
import { Icon, IconName } from './Icon';

type ToastKind = 'success' | 'error';

/** Reward-тост «плашка-гроссбух» (Антверпен 2.0 §7): название ← → сумма Fraunces. */
export interface RewardToast {
  icon?: IconName;
  title: string;
  /** Правый датум: «+150», «−150», «3 400 ₽»… Монетка добавляется флагом. */
  amount?: string;
  coin?: boolean;
  /** Крит-типографика заголовка (Fraunces WONK:1 19px). */
  crit?: boolean;
  /** Вторая строка под hairline — курсив. */
  sub?: string;
}

interface ToastState {
  id: number;
  kind: ToastKind;
  text?: string;
  reward?: RewardToast;
}

interface ToastApi {
  show: (text: string, kind?: ToastKind) => void;
  reward: (r: RewardToast) => void;
}

const ToastCtx = createContext<ToastApi | null>(null);

const AMOUNT_STYLE: React.CSSProperties = {
  fontFamily: theme.fontDisplay,
  fontVariationSettings: "'opsz' 144, 'WONK' 0",
  fontVariantNumeric: 'tabular-nums',
  fontSize: 17,
  whiteSpace: 'nowrap',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);

  const show = useCallback<ToastApi['show']>((text, kind = 'success') => {
    setToast({ id: Date.now(), kind, text });
  }, []);

  const reward = useCallback<ToastApi['reward']>((r) => {
    setToast({ id: Date.now(), kind: 'success', reward: r });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), toast.reward ? 4000 : 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const r = toast?.reward;

  return (
    <ToastCtx.Provider value={{ show, reward }}>
      {children}
      {toast && (
        <div
          key={toast.id}
          role="status"
          className="rise"
          style={{
            // плашка-гроссбух: тушь/paper, radius 2, БЕЗ тени, над нижней навигацией.
            // Центрирование left/right+margin, не transform — .rise его перетирает.
            position: 'fixed',
            left: 16,
            right: 16,
            margin: '0 auto',
            maxWidth: theme.maxWidth - 32,
            bottom: 'calc(74px + env(safe-area-inset-bottom))',
            background: theme.colors.ink,
            color: toast.kind === 'error' ? theme.colors.redSoft : theme.colors.paper,
            padding: '12px 16px',
            borderRadius: theme.radius.sm,
            fontFamily: theme.font,
            zIndex: 1000,
          }}
        >
          {r ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                {r.crit ? (
                  <span
                    style={{
                      fontFamily: theme.fontDisplay,
                      fontVariationSettings: "'opsz' 144, 'WONK' 1",
                      fontSize: 19,
                      letterSpacing: '.02em',
                    }}
                  >
                    {r.title}
                  </span>
                ) : (
                  <span style={{ fontSize: 13.5, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {r.icon && <Icon name={r.icon} size={15} />}
                    {r.title}
                  </span>
                )}
                {r.amount && (
                  <span style={{ ...AMOUNT_STYLE, color: r.crit ? theme.colors.greenSoft : undefined }}>
                    {r.amount}
                    {r.coin !== false && (
                      <>
                        {' '}
                        <Icon name="coin" size={13} inline />
                      </>
                    )}
                  </span>
                )}
              </div>
              {r.sub && (
                <div
                  style={{
                    borderTop: '1px solid rgba(236,233,223,.25)',
                    marginTop: 8,
                    paddingTop: 7,
                    fontSize: 12,
                    color: theme.colors.paperSub,
                    fontStyle: 'italic',
                  }}
                >
                  {r.sub}
                </div>
              )}
            </>
          ) : (
            <span style={{ fontSize: 14 }}>{toast.text}</span>
          )}
        </div>
      )}
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastApi {
  const v = useContext(ToastCtx);
  if (!v) throw new Error('useToast outside <ToastProvider>');
  return v;
}
